import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../services/api';
import OrdersComponent from './sales/OrdersComponent';
import ShippingManagement from './ShippingManagement';
import sharedAuth from '../services/auth.js';

interface SalesAnalytics {
  period: {
    start: string;
    end: string;
  };
  summary: {
    totalRevenue: number;
    totalTransactions: number;
    averageOrderValue: number;
    conversionRate: string;
    failedTransactions: number;
  };
  dailyData: Array<{
    date: string;
    revenue: number;
    transactions: number;
  }>;
  productSales: Record<string, {
    quantity: number;
    revenue: number;
  }>;
  geographicData: Record<string, {
    revenue: number;
    transactions: number;
  }>;
}

interface Transaction {
  id: string;
  amount: number;
  currency: string;
  status: string;
  created: number;
  customer: string;
  paymentMethod: string;
  metadata: Record<string, unknown>;
  shipping?: {
    address?: {
      country: string;
      state: string;
      city: string;
    };
  };
  receiptUrl?: string;
  refunded: boolean;
  refundAmount: number;
}

interface CustomerAnalytics {
  period: {
    start: string;
    end: string;
  };
  stats: {
    totalCustomers: number;
    newCustomers: number;
    repeatCustomers: number;
    averageCustomerValue: number;
    totalRevenue: number;
  };
  customerSegments: {
    newCustomers: number;
    repeatCustomers: number;
    highValueCustomers: number;
  };
}

interface RefundAnalytics {
  period: {
    start: string;
    end: string;
  };
  stats: {
    totalRefunds: number;
    totalRefundAmount: number;
    averageRefundAmount: number;
    refundRate: string;
  };
  refunds: Array<{
    id: string;
    amount: number;
    currency: string;
    status: string;
    reason: string;
    created: number;
    charge: string;
  }>;
}

interface DashboardData {
  today: {
    revenue: number;
    transactions: number;
    pendingPayments: number;
  };
  yesterday: {
    revenue: number;
    transactions: number;
    pendingPayments: number;
  };
  growth: {
    revenue: string;
    transactions: string;
  };
  realTime: {
    lastUpdated: string;
  };
}

interface SalesTransactionsSectionProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
}

const SalesTransactionsSection: React.FC<SalesTransactionsSectionProps> = ({
  activeTab,
  setActiveTab
}) => {
  const [salesAnalytics, setSalesAnalytics] = useState<SalesAnalytics | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [customerAnalytics, setCustomerAnalytics] = useState<CustomerAnalytics | null>(null);
  const [refundAnalytics, setRefundAnalytics] = useState<RefundAnalytics | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [componentError, setComponentError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    const handleError = (error: ErrorEvent) => {
      console.error('SalesTransactionsSection error:', error);
      setComponentError(error.message);
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  const fetchDashboardData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiService.getDashboardData();
      const payload = (response as unknown as Record<string, unknown>).dashboardData;
      if (response.success && payload) {
        setDashboardData(payload as DashboardData);
      } else {
        setError(response.message || 'Failed to fetch dashboard data');
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to fetch dashboard data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchSalesAnalytics = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiService.getSalesAnalytics(
        dateRange.startDate,
        dateRange.endDate,
        'day'
      );
      const payload = (response as unknown as { analytics?: SalesAnalytics }).analytics;
      if (response.success && payload) {
        setSalesAnalytics(payload as SalesAnalytics);
      } else {
        setError(response.message || 'Failed to fetch sales analytics');
      }
    } catch (error) {
      console.error('Error fetching sales analytics:', error);
      setError('Failed to fetch sales analytics. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [dateRange.startDate, dateRange.endDate]);

  const fetchTransactions = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiService.getTransactions({
        limit: 50,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });
      const payload = (response as unknown as { transactions?: Transaction[] }).transactions;
      if (response.success && payload) {
        setTransactions((payload as Transaction[]) || []);
      } else {
        setError(response.message || 'Failed to fetch transactions');
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setError('Failed to fetch transactions. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [dateRange.startDate, dateRange.endDate]);

  const fetchCustomerAnalytics = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiService.getCustomerAnalytics(
        dateRange.startDate,
        dateRange.endDate
      );
      const payload = (response as unknown as { customerAnalytics?: CustomerAnalytics }).customerAnalytics;
      if (response.success && payload) {
        setCustomerAnalytics(payload as CustomerAnalytics);
      } else {
        setError(response.message || 'Failed to fetch customer analytics');
      }
    } catch (error) {
      console.error('Error fetching customer analytics:', error);
      setError('Failed to fetch customer analytics. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [dateRange.startDate, dateRange.endDate]);

  const fetchRefundAnalytics = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiService.getRefundAnalytics(
        dateRange.startDate,
        dateRange.endDate
      );
      const payload = (response as unknown as { refundAnalytics?: RefundAnalytics }).refundAnalytics;
      if (response.success && payload) {
        setRefundAnalytics(payload as RefundAnalytics);
      } else {
        setError(response.message || 'Failed to fetch refund analytics');
      }
    } catch (error) {
      console.error('Error fetching refund analytics:', error);
      setError('Failed to fetch refund analytics. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [dateRange.startDate, dateRange.endDate]);

  useEffect(() => {
    if (activeTab === 'overview') {
      fetchDashboardData();
    } else if (activeTab === 'sales') {
      fetchSalesAnalytics();
    } else if (activeTab === 'transactions') {
      fetchTransactions();
    } else if (activeTab === 'customers') {
      fetchCustomerAnalytics();
    } else if (activeTab === 'refunds') {
      fetchRefundAnalytics();
    }
  }, [activeTab, dateRange, fetchDashboardData, fetchSalesAnalytics, fetchTransactions, fetchCustomerAnalytics, fetchRefundAnalytics]);

  // Orders is self-contained: it fetches from the Purchase collection rather
  // than the Stripe-backed endpoints the rest of this section shares, and it
  // owns its own loading and error state.
  if (activeTab === 'orders') {
    return <OrdersComponent />;
  }

  // Shipping is self-contained in the same way, and sits here rather than in
  // its own section because fulfilment is a stage of an order, not a separate
  // part of the business. It was previously reachable only from /admin.
  if (activeTab === 'shipping') {
    return <ShippingManagement />;
  }

  if (componentError) {
    return (
      <div className="text-center py-12">
        <div className="text-4xl mb-4">⚠️</div>
        <h2 className="text-2xl font-title font-bold text-deep-navy mb-2">Component Error</h2>
        <p className="text-brand-red mb-4">An error occurred while loading this section.</p>
        <p className="text-gray-500 text-sm mb-4">Error: {componentError}</p>
        <button
          onClick={() => setComponentError(null)}
          className="bg-brand-red hover:bg-brand-red-light text-white font-bold px-6 py-3 rounded-xl transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  const token = sharedAuth.getToken();
  const user = sharedAuth.getUser();

  if (!token || !user) {
    return (
      <div className="text-center py-12">
        <div className="text-4xl mb-4">🔒</div>
        <h2 className="text-2xl font-title font-bold text-deep-navy mb-2">Authentication Required</h2>
        <p className="text-brand-red mb-4">You must be logged in to access this section.</p>
        <p className="text-gray-500 text-sm">Token: {token ? 'Present' : 'Missing'}</p>
        <p className="text-gray-500 text-sm">User Data: {user ? 'Present' : 'Missing'}</p>
      </div>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount / 100);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'succeeded':
        return 'bg-ice-teal/30 text-brand-blue';
      case 'pending':
        return 'bg-blue-100 text-brand-blue-light';
      case 'failed':
      case 'canceled':
        return 'bg-red-100 text-brand-red';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  if (isLoading && activeTab === 'overview' && !dashboardData) {
    return (
      <div className="space-y-8">
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-600 text-lg">Loading dashboard data...</div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Sales & Transactions Overview */}
      {activeTab === 'overview' && (
        <div className="space-y-8">
          {error && (
            <div className="bg-red-50 border border-red-200 text-brand-red rounded-xl p-4">
              {error}
            </div>
          )}

          {dashboardData && (
            <div className="grid md:grid-cols-4 gap-6">
              <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm hover:shadow-md transition-shadow">
                <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Today's Revenue</h3>
                <div className="text-3xl font-bold text-brand-blue">
                  ${dashboardData.today.revenue.toFixed(2)}
                </div>
                <div className={`text-sm ${Number.parseFloat(dashboardData.growth.revenue) >= 0 ? 'text-scarf-green' : 'text-brand-red'}`}>
                  {dashboardData.growth.revenue}% vs yesterday
                </div>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm hover:shadow-md transition-shadow">
                <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Today's Transactions</h3>
                <div className="text-3xl font-bold text-brand-blue-light">
                  {dashboardData.today.transactions}
                </div>
                <div className={`text-sm ${Number.parseFloat(dashboardData.growth.transactions) >= 0 ? 'text-scarf-green' : 'text-brand-red'}`}>
                  {dashboardData.growth.transactions}% vs yesterday
                </div>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm hover:shadow-md transition-shadow">
                <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Pending Payments</h3>
                <div className="text-3xl font-bold text-brand-red">
                  {dashboardData.today.pendingPayments}
                </div>
                <div className="text-gray-500 text-sm">Awaiting completion</div>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm hover:shadow-md transition-shadow">
                <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Yesterday's Revenue</h3>
                <div className="text-3xl font-bold text-gray-600">
                  ${dashboardData.yesterday.revenue.toFixed(2)}
                </div>
                <div className="text-gray-500 text-sm">For comparison</div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Quick Actions</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <button
                onClick={() => setActiveTab('sales')}
                className="bg-brand-blue hover:bg-brand-blue-dark text-white font-bold px-6 py-3 rounded-xl transition-colors"
              >
                View Sales Analytics
              </button>
              <button
                onClick={() => setActiveTab('transactions')}
                className="bg-brand-blue-light hover:bg-brand-blue text-white font-bold px-6 py-3 rounded-xl transition-colors"
              >
                View Transactions
              </button>
              <button
                onClick={() => setActiveTab('customers')}
                className="bg-ice-teal-dark hover:bg-ice-teal text-white font-bold px-6 py-3 rounded-xl transition-colors"
              >
                Customer Analytics
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sales Analytics Tab */}
      {activeTab === 'sales' && (
        <div className="space-y-8">
          {error && (
            <div className="bg-red-50 border border-red-200 text-brand-red rounded-xl p-4">
              {error}
            </div>
          )}

          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <div className="flex flex-col md:flex-row gap-4 items-center">
              <div>
                <label htmlFor="sales-start-date" className="block text-deep-navy font-bold mb-2">Start Date</label>
                <input
                  id="sales-start-date"
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-xl text-gray-800 focus:outline-none focus:border-brand-blue"
                />
              </div>
              <div>
                <label htmlFor="sales-end-date" className="block text-deep-navy font-bold mb-2">End Date</label>
                <input
                  id="sales-end-date"
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-xl text-gray-800 focus:outline-none focus:border-brand-blue"
                />
              </div>
            </div>
          </div>

          {salesAnalytics && (
            <>
              <div className="grid md:grid-cols-5 gap-6">
                <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
                  <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Total Revenue</h3>
                  <div className="text-3xl font-bold text-brand-blue">
                    ${salesAnalytics.summary.totalRevenue.toFixed(2)}
                  </div>
                </div>
                <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
                  <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Transactions</h3>
                  <div className="text-3xl font-bold text-brand-blue-light">
                    {salesAnalytics.summary.totalTransactions}
                  </div>
                </div>
                <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
                  <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Avg Order Value</h3>
                  <div className="text-3xl font-bold text-brand-blue">
                    ${salesAnalytics.summary.averageOrderValue.toFixed(2)}
                  </div>
                </div>
                <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
                  <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Conversion Rate</h3>
                  <div className="text-3xl font-bold text-scarf-green">
                    {salesAnalytics.summary.conversionRate}%
                  </div>
                </div>
                <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
                  <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Failed Transactions</h3>
                  <div className="text-3xl font-bold text-brand-red">
                    {salesAnalytics.summary.failedTransactions}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
                <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Daily Revenue</h3>
                {salesAnalytics.dailyData.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-gray-500 text-lg">No daily data available</div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {salesAnalytics.dailyData.map((day) => (
                      <div key={day.date} className="flex justify-between items-center bg-frost-white rounded-xl p-3">
                        <div>
                          <div className="font-bold text-deep-navy">{new Date(day.date).toLocaleDateString()}</div>
                          <div className="text-gray-500 text-sm">{day.transactions} transactions</div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-brand-blue">${day.revenue.toFixed(2)}</div>
                          <div className="text-gray-500 text-sm">
                            ${(day.revenue / day.transactions).toFixed(2)} avg
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
                <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Product Performance</h3>
                <p className="text-gray-500 text-sm mb-4">Sorted by average price (revenue per unit)</p>
                {Object.keys(salesAnalytics.productSales).length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-gray-500 text-lg">No product sales data available</div>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {Object.entries(salesAnalytics.productSales)
                      .map(([productId, data]) => ({
                        productId,
                        ...data,
                        avgPrice: data.quantity > 0 ? data.revenue / data.quantity : 0
                      }))
                      .sort((a, b) => b.avgPrice - a.avgPrice)
                      .map(({ productId, revenue, quantity, avgPrice }) => (
                        <div key={productId} className="bg-frost-white rounded-xl p-4">
                          <h4 className="font-bold text-deep-navy mb-2">{productId}</h4>
                          <div className="text-2xl font-bold text-brand-blue">${revenue.toFixed(2)}</div>
                          <div className="text-gray-500 text-sm">{quantity} units sold</div>
                          <div className="text-brand-blue-light text-sm font-semibold">
                            ${avgPrice.toFixed(2)} avg price
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
                <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Geographic Performance</h3>
                <p className="text-gray-500 text-sm mb-4">Sorted by average transaction value</p>
                {Object.keys(salesAnalytics.geographicData).length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-gray-500 text-lg">No geographic data available</div>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {Object.entries(salesAnalytics.geographicData)
                      .map(([country, data]) => ({
                        country,
                        ...data,
                        avgTransactionValue: data.transactions > 0 ? data.revenue / data.transactions : 0
                      }))
                      .sort((a, b) => b.avgTransactionValue - a.avgTransactionValue)
                      .map(({ country, revenue, transactions, avgTransactionValue }) => (
                        <div key={country} className="bg-frost-white rounded-xl p-4">
                          <h4 className="font-bold text-deep-navy mb-2">{country}</h4>
                          <div className="text-2xl font-bold text-brand-blue-light">${revenue.toFixed(2)}</div>
                          <div className="text-gray-500 text-sm">{transactions} transactions</div>
                          <div className="text-brand-blue text-sm font-semibold">
                            ${avgTransactionValue.toFixed(2)} avg transaction
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Transactions Tab */}
      {activeTab === 'transactions' && (
        <div className="space-y-8">
          {error && (
            <div className="bg-red-50 border border-red-200 text-brand-red rounded-xl p-4">
              {error}
            </div>
          )}

          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Recent Transactions</h3>
            {transactions.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-gray-500 text-lg">No transactions found</div>
                <div className="text-gray-400 text-sm mt-2">Transactions will appear here once they occur</div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-ice-teal-light">
                      <th className="text-left py-3 px-4 text-deep-navy font-bold">Transaction ID</th>
                      <th className="text-left py-3 px-4 text-deep-navy font-bold">Amount</th>
                      <th className="text-left py-3 px-4 text-deep-navy font-bold">Status</th>
                      <th className="text-left py-3 px-4 text-deep-navy font-bold">Date</th>
                      <th className="text-left py-3 px-4 text-deep-navy font-bold">Customer</th>
                      <th className="text-left py-3 px-4 text-deep-navy font-bold">Location</th>
                      <th className="text-left py-3 px-4 text-deep-navy font-bold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((transaction) => (
                      <tr key={transaction.id} className="border-b border-gray-100 hover:bg-frost-white transition-colors">
                        <td className="py-3 px-4">
                          <div className="font-mono text-sm text-gray-800">{transaction.id}</div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="font-bold text-deep-navy">{formatCurrency(transaction.amount)}</div>
                          {transaction.refunded && (
                            <div className="text-brand-red text-sm">
                              Refunded: {formatCurrency(transaction.refundAmount)}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded text-xs font-bold ${getStatusColor(transaction.status)}`}>
                            {transaction.status.toUpperCase()}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-gray-700 text-sm">
                          {formatDate(transaction.created)}
                        </td>
                        <td className="py-3 px-4">
                          <div className="font-mono text-sm text-gray-800">{transaction.customer}</div>
                        </td>
                        <td className="py-3 px-4 text-gray-700 text-sm">
                          {transaction.shipping?.address ?
                            `${transaction.shipping.address.city}, ${transaction.shipping.address.country}` :
                            'N/A'
                          }
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex space-x-2">
                            {transaction.receiptUrl && (
                              <a
                                href={transaction.receiptUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-2 py-1 bg-brand-blue-light hover:bg-brand-blue text-white rounded text-xs font-bold transition-colors"
                              >
                                Receipt
                              </a>
                            )}
                            <button className="px-2 py-1 bg-brand-blue hover:bg-brand-blue-dark text-white rounded text-xs font-bold transition-colors">
                              View
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Customer Analytics Tab */}
      {activeTab === 'customers' && (
        <div className="space-y-8">
          {error && (
            <div className="bg-red-50 border border-red-200 text-brand-red rounded-xl p-4">
              {error}
            </div>
          )}

          {customerAnalytics && (
            <>
              <div className="grid md:grid-cols-4 gap-6">
                <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
                  <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Total Customers</h3>
                  <div className="text-3xl font-bold text-brand-blue">
                    {customerAnalytics.stats.totalCustomers}
                  </div>
                </div>
                <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
                  <h3 className="text-lg font-title font-bold text-deep-navy mb-2">New Customers</h3>
                  <div className="text-3xl font-bold text-brand-blue-light">
                    {customerAnalytics.stats.newCustomers}
                  </div>
                </div>
                <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
                  <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Repeat Customers</h3>
                  <div className="text-3xl font-bold text-scarf-green">
                    {customerAnalytics.stats.repeatCustomers}
                  </div>
                </div>
                <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
                  <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Avg Customer Value</h3>
                  <div className="text-3xl font-bold text-brand-blue">
                    ${customerAnalytics.stats.averageCustomerValue.toFixed(2)}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
                <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Customer Segments</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-frost-white rounded-xl p-4">
                    <h4 className="font-bold text-deep-navy mb-2">New Customers</h4>
                    <div className="text-2xl font-bold text-brand-blue-light">
                      {customerAnalytics.customerSegments.newCustomers}
                    </div>
                    <div className="text-gray-500 text-sm">First-time buyers</div>
                  </div>
                  <div className="bg-frost-white rounded-xl p-4">
                    <h4 className="font-bold text-deep-navy mb-2">Repeat Customers</h4>
                    <div className="text-2xl font-bold text-scarf-green">
                      {customerAnalytics.customerSegments.repeatCustomers}
                    </div>
                    <div className="text-gray-500 text-sm">Multiple purchases</div>
                  </div>
                  <div className="bg-frost-white rounded-xl p-4">
                    <h4 className="font-bold text-deep-navy mb-2">High Value</h4>
                    <div className="text-2xl font-bold text-brand-blue">
                      {customerAnalytics.customerSegments.highValueCustomers}
                    </div>
                    <div className="text-gray-500 text-sm">$50+ spent</div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Refunds Tab */}
      {activeTab === 'refunds' && (
        <div className="space-y-8">
          {error && (
            <div className="bg-red-50 border border-red-200 text-brand-red rounded-xl p-4">
              {error}
            </div>
          )}

          {refundAnalytics && (
            <>
              <div className="grid md:grid-cols-4 gap-6">
                <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
                  <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Total Refunds</h3>
                  <div className="text-3xl font-bold text-brand-red">
                    {refundAnalytics.stats.totalRefunds}
                  </div>
                </div>
                <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
                  <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Total Refund Amount</h3>
                  <div className="text-3xl font-bold text-brand-red">
                    ${refundAnalytics.stats.totalRefundAmount.toFixed(2)}
                  </div>
                </div>
                <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
                  <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Avg Refund Amount</h3>
                  <div className="text-3xl font-bold text-brand-blue-light">
                    ${refundAnalytics.stats.averageRefundAmount.toFixed(2)}
                  </div>
                </div>
                <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
                  <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Refund Rate</h3>
                  <div className="text-3xl font-bold text-brand-red">
                    {refundAnalytics.stats.refundRate}%
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
                <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Recent Refunds</h3>
                {refundAnalytics.refunds.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-gray-500 text-lg">No refunds found</div>
                    <div className="text-gray-400 text-sm mt-2">Refunds will appear here once they occur</div>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-ice-teal-light">
                          <th className="text-left py-3 px-4 text-deep-navy font-bold">Refund ID</th>
                          <th className="text-left py-3 px-4 text-deep-navy font-bold">Amount</th>
                          <th className="text-left py-3 px-4 text-deep-navy font-bold">Status</th>
                          <th className="text-left py-3 px-4 text-deep-navy font-bold">Reason</th>
                          <th className="text-left py-3 px-4 text-deep-navy font-bold">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {refundAnalytics.refunds.map((refund) => (
                          <tr key={refund.id} className="border-b border-gray-100 hover:bg-frost-white transition-colors">
                            <td className="py-3 px-4">
                              <div className="font-mono text-sm text-gray-800">{refund.id}</div>
                            </td>
                            <td className="py-3 px-4">
                              <div className="font-bold text-brand-red">
                                ${refund.amount.toFixed(2)}
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 rounded text-xs font-bold ${
                                refund.status === 'succeeded' ? 'bg-ice-teal/30 text-brand-blue' : 'bg-red-100 text-brand-red'
                              }`}>
                                {refund.status.toUpperCase()}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-gray-700 text-sm">
                              {refund.reason.replace(/_/g, ' ')}
                            </td>
                            <td className="py-3 px-4 text-gray-700 text-sm">
                              {formatDate(refund.created)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default SalesTransactionsSection;
